import React from "react";

import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import dayjs, { Dayjs } from "dayjs";
import { IoChevronDownOutline, IoChevronUpOutline } from "react-icons/io5";
import { useDispatch } from "react-redux";
import { makeStyles } from "tss-react/mui";

import DownloadStatement from "./DownloadStatement";
import RaiseQuery from "./RaiseQuery";
import Transactions from "./Transactions";
import {
  downloadStatement,
  downloadMaguireStatement,
} from "../../store/modules/statementReport/statementReportActions";
import theme from "../../theme";
import { StatementInformationDetail, StatementTransaction } from "../../types/types";
import { formatCurrency, getStyleColor } from "../../utils/helpers";
import { useIsMaguireEnabled, useMaguireWeeklyBillingCycleCutOffId } from "../../utils/maguireHelper";
import { ButtonPrimary } from "../Common/Buttons/Buttons";

const useStyles = makeStyles()((theme) => ({
  root: {
    flexGrow: 2,
  },
  control: {
    padding: theme.spacing(4),
  },
  accordion: {
    backgroundColor: getStyleColor("--custom-color-light-grey"),
    border: 0,
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    "&::before": {
      display: "none",
    },
    // overrides MUI's default of only rounding the first/last accordion in a group
    "&:first-of-type, &:last-of-type": {
      borderRadius: 12,
    },
  },
  accordionSummary: {
    padding: "24px",
    borderRadius: 12,
  },
  accordionDetails: {
    padding: "0px 24px 24px 24px",
  },
  chip: {
    borderRadius: 3,
    border: 0,
  },
}));

export interface BillingCycleProps {
  totalNumberOfBookings: number;
  billingPeriodEnd: Dayjs | string;
  billingCycleStatus: string;
  statusColor: string;
  totalBookingsAmount: number;
  totalCCSAndACCSAmount: number;
  totalOtherFees: number;
  totalAmount: number;
  transactions: StatementTransaction[] | StatementInformationDetail[];
  billingCycleId: number;
  customerAccountId: number;
  debtCategoryId?: number;
  statementId?: number;
  isWeeklyStatement?: boolean;
}

const BillingCycle = (props: BillingCycleProps) => {
  const [showQuery, setShowQuery] = React.useState(false);
  const [expanded, setExpanded] = React.useState(false);
  const dispatch = useDispatch();
  const isMaguireEnabled = useIsMaguireEnabled();
  const weeklyBillingCycleCutOffId = useMaguireWeeklyBillingCycleCutOffId();

  const handleQueryClicked = () => {
    setShowQuery((prev) => !prev);
  };
  const isWeekly = props.isWeeklyStatement === true;
  const handleDownloadClicked = () => {
    if (isMaguireEnabled) {
      if (isWeekly && props.billingCycleId >= weeklyBillingCycleCutOffId) {
        downloadMaguireStatement(dispatch, props.customerAccountId, props.statementId!, props.billingCycleId);
      } else {
        downloadStatement(dispatch, props.customerAccountId, props.billingCycleId);
      }
    } else {
      downloadStatement(dispatch, props.customerAccountId, props.billingCycleId);
    }
  };

  const { classes } = useStyles();

  return (
    <Accordion
      className={classes.accordion}
      key={props.billingCycleId}
      elevation={0}
      disableGutters
      expanded={expanded}
      onChange={(_, isExpanded) => setExpanded(isExpanded)}
    >
      <AccordionSummary className={classes.accordionSummary}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs>
            <Typography style={{ color: theme.palette.text.primary }}>
              {typeof props.billingPeriodEnd === "string"
                ? props.billingPeriodEnd
                : dayjs(props.billingPeriodEnd).format("D MMM YYYY")}
            </Typography>
          </Grid>
          <Grid item sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {props.billingCycleStatus && (
              <Chip
                label={props.billingCycleStatus}
                className={classes.chip}
                style={{ backgroundColor: props.statusColor }}
                variant="outlined"
              />
            )}
            <Box component="span" sx={{ display: "flex", fontSize: 24 }}>
              {expanded ? <IoChevronUpOutline /> : <IoChevronDownOutline />}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Divider></Divider>
          </Grid>
          <Grid item xs={4} md={3}>
            {formatCurrency(props.totalBookingsAmount)}
            <br />
            <span style={{ color: theme.palette.info.dark, fontSize: 14 }}>BOOKINGS</span>
          </Grid>
          <Grid item xs={4} md={3}>
            {formatCurrency(props.totalCCSAndACCSAmount)}
            <br />
            <span style={{ color: theme.palette.info.dark, fontSize: 14 }}>CCS + ACCS</span>
          </Grid>
          <Grid item xs={4} md={3}>
            {formatCurrency(props.totalOtherFees)}
            <br />
            <span style={{ color: theme.palette.info.dark, fontSize: 14 }}>OTHER</span>
          </Grid>
          <Grid item xs={12} md={3}>
            <>
              <strong>{formatCurrency(props.totalAmount)}</strong>
              <br />
              <span style={{ color: theme.palette.info.dark, fontSize: 14 }}>TOTAL</span>
            </>
          </Grid>
        </Grid>
      </AccordionSummary>
      <AccordionDetails className={classes.accordionDetails}>
        <Grid container>
          <Grid item xs={12}>
            <DownloadStatement
              onDownload={handleDownloadClicked}
              key={props.billingCycleId}
              customerAccountId={props.customerAccountId}
              billingCycleId={props.billingCycleId}
              {...(isMaguireEnabled
                ? {
                    statementId: props.statementId!,
                    isWeeklyStatement: props.isWeeklyStatement,
                  }
                : {})}
            />
            <Collapse in={!showQuery}>
              <ButtonPrimary hidden={showQuery} onClick={handleQueryClicked} fullWidth style={{ marginTop: 0 }}>
                <strong> Raise Query</strong>
              </ButtonPrimary>
            </Collapse>
            <Box paddingBottom={3}>
              <RaiseQuery
                billingId={props.billingCycleId}
                showForm={showQuery}
                transactions={props.transactions}
                handleDismiss={handleQueryClicked}
              />
            </Box>
          </Grid>
          {/* Conditional rendering based on isMaguireEnabled */}
          {Array.isArray(props.transactions) && props.transactions.length > 0 && (
            <>
              {isMaguireEnabled ? (
                isWeekly ? (
                  <Transactions
                    transactions={props.transactions as StatementInformationDetail[]}
                    isWeeklyStatement={props.isWeeklyStatement}
                  />
                ) : (
                  <Transactions
                    transactions={props.transactions as StatementTransaction[]}
                    isWeeklyStatement={props.isWeeklyStatement}
                  />
                )
              ) : (
                <Transactions
                  transactions={props.transactions as StatementTransaction[]}
                  isWeeklyStatement={props.isWeeklyStatement}
                />
              )}
            </>
          )}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default BillingCycle;
